import React, { useMemo } from "react";

import { HeaderBreadcrumbRoutes } from "../common/ui/layout/header/header-breadcrumb/HeaderBreadcrumb";
import { ProtectedRoute, RouteItem } from "../core/routing/ProtectedRoute";
import { SprintAccessRight } from "../core/security/SprintAccessRight";
import { AirlineListingDashboard } from "./airline/listing/AirlineListingDashboard";
import { DistributorsDashboard } from "./distributors/DistributorsDashboard";
import { EncodeSpecificationDashboard } from "./encode-specification/EncodeSpecificationsDashboard";
import { FramerateProfilesDashboard } from "./framerate-profiles/FramerateProfilesDashboard";
import { IntegratorDashboard } from "./integrator/listing/IntegratorDashboard";
import { IntegratorDetailsDashboard } from "./integrator/listing/IntegratorDetailsDashboard";
import { LanguageSpecificationDashboardPage } from "./language-specification/dashboard/LanguageSpecificationDashboardPage";
import { LanguageSpecificationPage } from "./language-specification/listing/LanguageSpecificationPage";
import { LanguageDashboard } from "./language/listing/LanguageDashboard";
import { ManzanitaPresetListingPage } from "./manzanita-presets/listing/ManzanitaPresetListingPage";
import { MuxSpecificationDashboardPage } from "./mux-specification/dashboard/MuxSpecificationDashboardPage";
import { MuxSpecificationListingPage } from "./mux-specification/listing/MuxSpecificationListingPage";
import { OriginatorDashboard } from "./originator/listing/OriginatorDashboard";
import { ResolutionsListingPage } from "./resolutions/listing/ResolutionsListingPage";
import { SystemPlatformDetailsPage } from "./system-platforms/details/SystemPlatformDetailsPage";
import { SystemPlatformsListingPage } from "./system-platforms/listing/SystemPlatformListingPage";
import { TEMDashboard } from "./tem/TEMDashboard";
import { UsersDashboard } from "./users/listing/UsersDashboard";
const adminBasePath = "/admin";

const adminMinRight = [SprintAccessRight.AdminReadWrite];

export const adminLanguagesRoute: RouteItem = {
    path: `${adminBasePath}/languages`,
    component: LanguageDashboard,
    accessRights: adminMinRight,
    name: "Languages"
};

export const adminAirlinesRoute: RouteItem = {
    path: `${adminBasePath}/airlines`,
    component: AirlineListingDashboard,
    accessRights: adminMinRight,
    name: "Airlines"
};

export const adminSystemPlatformsRoute: RouteItem = {
    path: `${adminBasePath}/airlines/:airlineId`,
    component: SystemPlatformsListingPage,
    accessRights: adminMinRight,
    name: "Airline Details"
};

export const adminSystemPlatformDetailsRoute: RouteItem = {
    path: `${adminBasePath}/airlines/:airlineId/system-platforms/:systemId`,
    component: SystemPlatformDetailsPage,
    accessRights: adminMinRight,
    name: "System Platform Details"
};

export const adminIntegratorsRoute: RouteItem = {
    path: `${adminBasePath}/integrators`,
    component: IntegratorDashboard,
    accessRights: adminMinRight,
    name: "Integrators"
};

export const adminIntegratorDetailsRoute: RouteItem = {
    path: `${adminBasePath}/integrators/:integratorId`,
    component: IntegratorDetailsDashboard,
    accessRights: adminMinRight,
    name: "Integrator Details"
};

export const adminEncodeSpecificationRoute: RouteItem = {
    path: `${adminBasePath}/encode-specifications`,
    component: EncodeSpecificationDashboard,
    accessRights: adminMinRight,
    name: "Encode Specifications"
};

export const adminMuxSpecificationListingRoute: RouteItem = {
    path: `${adminBasePath}/mux-specifications`,
    component: MuxSpecificationListingPage,
    accessRights: adminMinRight,
    name: "Mux Specifications"
};

export const adminMuxSpecificationDashboardRoute: RouteItem = {
    path: `${adminBasePath}/mux-specifications/:muxSpecificationId`,
    component: MuxSpecificationDashboardPage,
    accessRights: adminMinRight,
    name: "Details"
};

export const adminManzanitaPresetsListingRoute: RouteItem = {
    path: `${adminBasePath}/manzanita-presets`,
    component: ManzanitaPresetListingPage,
    accessRights: adminMinRight,
    name: "Presets"
};

export const adminLanguageSpecificationsListingRoute: RouteItem = {
    path: `${adminBasePath}/language-specifications`,
    component: LanguageSpecificationPage,
    accessRights: adminMinRight,
    name: "Language Specifications"
};

export const adminLanguageSpecificationDashboardRoute: RouteItem = {
    path: `${adminBasePath}/language-specifications/:languageSpecificationId`,
    component: LanguageSpecificationDashboardPage,
    accessRights: adminMinRight,
    name: "Details"
};

export const adminOriginatorsRoute: RouteItem = {
    path: `${adminBasePath}/originators`,
    component: OriginatorDashboard,
    accessRights: adminMinRight,
    name: "Originators"
};

export const adminDistributorsListingRoute: RouteItem = {
    path: `${adminBasePath}/distributors`,
    component: DistributorsDashboard,
    accessRights: adminMinRight,
    name: "Distributors"
};

export const adminFramerateProfilesListingRoute: RouteItem = {
    path: `${adminBasePath}/framerate-profiles`,
    component: FramerateProfilesDashboard,
    accessRights: adminMinRight,
    name: "Framerate Profiles"
};

export const adminResolutionsListingRoute: RouteItem = {
    path: `${adminBasePath}/resolutions`,
    component: ResolutionsListingPage,
    accessRights: adminMinRight,
    name: "Resolutions"
};

export const adminUsersADFSListingRoute: RouteItem = {
    path: `${adminBasePath}/users`,
    component: UsersDashboard,
    accessRights: adminMinRight,
    name: "Users list"
};

export const adminTEMRoute: RouteItem = {
    path: `${adminBasePath}/tem`,
    component: TEMDashboard,
    accessRights: adminMinRight,
    name: "TEM"
};

export const adminRoutes: HeaderBreadcrumbRoutes = {
    routeItems: [
        adminLanguagesRoute,
        adminAirlinesRoute,
        adminSystemPlatformsRoute,
        adminSystemPlatformDetailsRoute,
        adminEncodeSpecificationRoute,
        adminIntegratorsRoute,
        adminIntegratorDetailsRoute,
        adminMuxSpecificationListingRoute,
        adminMuxSpecificationDashboardRoute,
        adminManzanitaPresetsListingRoute,
        adminOriginatorsRoute,
        adminDistributorsListingRoute,
        adminFramerateProfilesListingRoute,
        adminResolutionsListingRoute,
        adminLanguageSpecificationsListingRoute,
        adminLanguageSpecificationDashboardRoute,
        adminUsersADFSListingRoute,
        adminTEMRoute
    ],
    baseRouteNames: ["Admin"]
};

export const AdminRoutes: React.FunctionComponent = () => {
    const protectedRoutes = useMemo(
        () => adminRoutes.routeItems.map((item) => <ProtectedRoute key={item.path} protectedRouteData={item} />),
        [adminRoutes.routeItems, adminRoutes.routeItems.length]
    );

    return <div>{protectedRoutes}</div>;
};

export const getIntegratorDetailsDashboardHref = (integratorId: string): string => {
    return adminIntegratorDetailsRoute.path.replace(":integratorId", integratorId);
};

export const getAirlineDetailsDashboardHref = (airlineId: string): string => {
    return adminSystemPlatformsRoute.path.replace(":airlineId", airlineId);
};

export const getSystemPlatformDetailsDashboardHref = (airlineId: string, systemId: string): string => {
    return adminSystemPlatformDetailsRoute.path.replace(":airlineId", airlineId).replace(":systemId", systemId);
};

export const getMuxSpecificationDashboardHref = (muxSpecificationId: string): string => {
    return adminMuxSpecificationDashboardRoute.path.replace(":muxSpecificationId", muxSpecificationId);
};

export const getLanguageSpecificationDashboardHref = (languageSpecificationId: string): string => {
    return adminLanguageSpecificationDashboardRoute.path.replace(":languageSpecificationId", languageSpecificationId);
};
